import { Injectable } from '@angular/core';
import { DataUser, User } from '@auth/interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class JwtHelperService {
  /**
   * Decodifica un JWT token y extrae el payload
   * @param token - El JWT token a decodificar
   * @returns El payload decodificado del token
   */
  decodeToken(token: string): any {
    try {
      // Dividir el token en sus tres partes
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Token JWT inválido');
      }

      // Decodificar la parte del payload (segunda parte)
      const payload = parts[1];

      // Reemplazar caracteres para que sea compatible con base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

      // Decodificar base64 y parsear JSON
      const decodedPayload = JSON.parse(window.atob(base64));

      return decodedPayload;
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      return null;
    }
  }

  /**
   * Extrae la información del usuario del token JWT
   * @param token - El JWT token
   * @returns La información del usuario extraída del token
   */
  extractUserFromToken(token: string): User | null {
    try {
      const payload = this.decodeToken(token);

      if (!payload) {
        return null;
      }

      // Verificar que el payload tiene la estructura esperada
      if (!payload.dataUser || !payload.role) {
        console.error(
          'El token no contiene la información de usuario esperada',
        );
        return null;
      }

      return {
        role: payload.role,
        dataUser: payload.dataUser,
        sub: payload.sub,
        iat: payload.iat,
        exp: payload.exp,
      };
    } catch (error) {
      console.error('Error al extraer usuario del token:', error);
      return null;
    }
  }

  /**
   * Verifica si el token ha expirado
   * @param token - El JWT token
   * @returns true si el token ha expirado, false si no
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      if (!payload || !payload.exp) {
        return true;
      }

      // Convertir la fecha de expiración a milisegundos
      const expirationDate = payload.exp * 1000;
      const currentDate = Date.now();

      return currentDate >= expirationDate;
    } catch (error) {
      console.error('Error al verificar expiración del token:', error);
      return true;
    }
  }

  /**
   * Guarda la información del usuario en localStorage
   * @param token - El JWT token
   * @returns true si se guardó correctamente, false si no
   */
  saveUserToLocalStorage(token: string): boolean {
    try {
      const user = this.extractUserFromToken(token);

      if (!user) {
        console.error(
          'No se pudo extraer la información del usuario del token',
        );
        return false;
      }

      // Guardar el dataUser en localStorage como 'user'
      localStorage.setItem('user', JSON.stringify(user.dataUser));

      return true;
    } catch (error) {
      console.error('Error al guardar usuario en localStorage:', error);
      return false;
    }
  }

  /**
   * Obtiene la información del usuario desde localStorage
   * @returns La información del usuario o null si no existe
   */
  getUserFromLocalStorage(): DataUser | null {
    try {
      const userString = localStorage.getItem('user');
      if (!userString) {
        return null;
      }

      return JSON.parse(userString) as DataUser;
    } catch (error) {
      console.error('Error al obtener usuario de localStorage:', error);
      return null;
    }
  }
}
